// apps/web/src/lib/financeiro-badge.ts — Fase 07, Plano 06 (OMIE-08, D-04, UI-SPEC).

export type EstadoBadgeFinanceiro = 'nenhum' | 'inadimplente' | 'bloqueado';

export interface BadgeFinanceiroInfo {
  readonly estado: EstadoBadgeFinanceiro;
  readonly rotulo: string | null;
  readonly icone: 'alert-triangle' | 'ban' | null;
}

export interface DadosFinanceiroBadgeInput {
  readonly situacaoCredito?: {
    readonly bloqueado?: boolean | null;
    readonly inadimplente?: boolean | null;
  } | null;
  readonly contasReceber?: {
    readonly totalVencido?: number | null;
  } | null;
  readonly inadimplente?: boolean | null;
}

export interface SessionWithPermissions {
  readonly permissions?: readonly string[] | string[] | null;
  readonly user?: {
    readonly permissions?: readonly string[] | string[] | null;
  } | null;
}

/**
 * Mapeia o estado financeiro de um cliente para o respectivo badge.
 * Em situação regular, retorna 'nenhum' (ausência de badge é o sinal visual de adimplência).
 */
export function resolverBadgeFinanceiro(dados?: DadosFinanceiroBadgeInput | null): BadgeFinanceiroInfo {
  if (!dados) {
    return { estado: 'nenhum', rotulo: null, icone: null };
  }

  if (dados.situacaoCredito?.bloqueado) {
    return {
      estado: 'bloqueado',
      rotulo: 'Bloqueado (Omie)',
      icone: 'ban',
    };
  }

  const temVencido = Number(dados.contasReceber?.totalVencido || 0) > 0;
  const isInadimplente = Boolean(dados.inadimplente || dados.situacaoCredito?.inadimplente || temVencido);

  if (isInadimplente) {
    return {
      estado: 'inadimplente',
      rotulo: 'Inadimplente',
      icone: 'alert-triangle',
    };
  }

  return { estado: 'nenhum', rotulo: null, icone: null };
}

/**
 * Determina se a sessão atual possui a permissão 'financeiro:consultar' (Gestão / Admin).
 * Caso não possua, o DOM financeiro deve ser 100% omitido (sem ícones de cadeado).
 */
export function deveExibirFinanceiro(session?: SessionWithPermissions | null): boolean {
  if (!session) return false;
  const permissions = session.permissions ?? session.user?.permissions;
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.includes('financeiro:consultar');
}

/**
 * Formata CPF ou CNPJ com pontuação padrão.
 */
export function formatarDocumentoFiscal(doc?: string | null): string {
  if (!doc) return '—';
  const clean = doc.replace(/\D/g, '');
  if (clean.length === 14) {
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (clean.length === 11) {
    return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return doc;
}

/**
 * Formata valor monetário em BRL.
 */
export function formatarMoeda(valor?: number | null): string {
  const num = Number(valor || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
}

/**
 * Formata o carimbo de atualização financeira cobrindo as 3 camadas.
 * Segue estritamente os textos do UI-SPEC Copywriting Contract.
 */
export function formatarTimestampFinanceiro(dataIso?: string | null): {
  readonly texto: string;
  readonly sincronizado: boolean;
} {
  if (!dataIso) {
    return {
      texto: 'Dados financeiros ainda não sincronizados.',
      sincronizado: false,
    };
  }

  try {
    const data = new Date(dataIso);
    if (isNaN(data.getTime())) {
      return {
        texto: 'Dados financeiros ainda não sincronizados.',
        sincronizado: false,
      };
    }

    const horaFormatada = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(data);

    return {
      texto: `Atualizado às ${horaFormatada}`,
      sincronizado: true,
    };
  } catch {
    return {
      texto: 'Dados financeiros ainda não sincronizados.',
      sincronizado: false,
    };
  }
}
